import { useCallback, useEffect, useState } from "react";
import {
  findLinkedinInvitation,
  findLinkedinInvitationAndGenerate,
  findLinkedinMessage,
  findLinkedinMessageAndGenerate,
  findSMS,
} from "../../api/ModelsApi";
import {
  ActionIcon,
  Divider,
  Group,
  HoverCard,
  LoadingOverlay,
  Overlay,
  Paper,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { replaceTokens } from "./previewUtils";
import { IconRotateClockwise, IconSend } from "@tabler/icons-react";
import { useLanguage } from "../../stores/LanguageStore";
import MessageAttachments from "../../pages/protected/leadsTracking/prospect/sheet/Right/answers/email/MessageAttachments";
import { findFile } from "../../api/FIleAPI";
import ModelEditor from "../Adder/ModelEditor";

const MessagePreviewer = ({
  model_id,
  type,
  senderSize = "sm",
  text,
  file_id,
  sender,
  withExample,
  contact,
  mah,
}) => {
  const lang = useLanguage((s) => s.language);
  const [modelProfile, setModelProfile] = useState({ text, sender, file_id });
  const [attachedFile, setAttachedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingGen, setLoadingGen] = useState(false);

  const handleGen = useCallback(() => {
    setLoadingGen(true);
    setLoading(true);

    const func =
      type === "linkedin_invitation"
        ? findLinkedinInvitationAndGenerate
        : findLinkedinMessageAndGenerate;
    func({
      id: model_id,
      contact_id: contact?.id,
    })
      .then((res) => {
        setModelProfile(res.data.model_profile);
      })
      .finally(() => {
        setLoadingGen(false);
        setLoading(false);
      });
  }, [model_id, contact?.id, type]);

  useEffect(() => {
    if (modelProfile?.is_ai && contact?.id) {
      handleGen();
    }
  }, [contact, handleGen, modelProfile?.is_ai]);

  useEffect(() => {
    if (model_id) {
      setLoading(true);
      let func;
      if (type === "linkedin_invitation") func = findLinkedinInvitation;
      if (type === "linkedin_message") func = findLinkedinMessage;
      if (type === "linkedin_vocal") func = findLinkedinMessage;
      if (type === "sms") func = findSMS;
      func(model_id)
        .then((response) => {
          setModelProfile(response.data.model_profile);
        })
        .finally(() => setLoading(false));
    } else if (!text) {
      setModelProfile(null);
    }
  }, [model_id, type, text]);

  useEffect(() => {
    if (modelProfile?.file_id || file_id) {
      findFile(modelProfile?.file_id || file_id).then((fileRes) => {
        setAttachedFile(fileRes.data.file_profile);
      });
    }
  }, [modelProfile?.file_id, file_id]);

  return (
    <Stack
      pos={"relative"}
      gap={"sm"}
      flex={1}
      w={"100%"}
      mah={mah}
      style={{
        border: "1px solid #00000018",
        overflow: "auto",
        borderRadius: 12,
      }}
      px={12}
      pt={12}
    >
      <LoadingOverlay visible={loading || loadingGen} />
      {type === "sms" && (
        <TextInput
          value={sender || modelProfile?.sender || ""}
          readOnly
          size={senderSize}
          placeholder={lang.components.email_preview.sender}
          leftSection={<IconSend size={16} />}
          w={300}
        />
      )}

      {withExample && (
        <Paper
          withBorder
          style={{ boxShadow: "#00000018 1px 2px 4px", overflow: "hidden" }}
          bg={"var(--mantine-color-gray-1)"}
          radius={"md"}
          p={"sm"}
          py={"xs"}
          w={"fit-content"}
          mr={"auto"}
          miw={100}
          maw={300}
          mt={"auto"}
          pos={"relative"}
        >
          <Overlay blur={5} backgroundOpacity={0} />
          Lorem ipsum dolor sit amet consectetur adipisicing elit. Veniam
          aspernatur minus facere dolor.
        </Paper>
      )}

      {modelProfile?.is_ai && (
        <>
          <Group pos={"relative"} gap={0} w={"100%"}>
            <HoverCard width={1000} zIndex={600}>
              <HoverCard.Target>
                <Text ml={"xs"} mt={6} flex={"1 1 auto"} truncate mr={16} w={0}>
                  Prompt : <b>{modelProfile?.prompt}</b>
                </Text>
              </HoverCard.Target>
              <HoverCard.Dropdown>
                <Text size={"md"}>{modelProfile?.prompt}</Text>
              </HoverCard.Dropdown>
            </HoverCard>

            <ModelEditor
              model={modelProfile}
              onModelEdited={({ closer }) => {
                closer?.();
                setLoading(true);
                handleGen?.();
              }}
              type={type}
              variant="light"
              radius={0}
            />

            {contact && (
              <Tooltip label="Regénérer le modèle">
                <ActionIcon
                  top={0}
                  right={0}
                  radius={0}
                  size={"lg"}
                  h={"100%"}
                  loading={loadingGen}
                  onClick={handleGen}
                >
                  <IconRotateClockwise size={18} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
          <Divider />
        </>
      )}

      <Paper
        bg={"var(--mantine-color-primary-0)"}
        radius={"md"}
        p={"sm"}
        py={"xs"}
        w={"fit-content"}
        ml={"auto"}
        miw={200}
        maw={"80%"}
        mt={16}
      >
        {replaceTokens({
          text: modelProfile?.text,
          contact,
          shouldEncode: false,
        })
          .split("\n")
          .map((x, i) => (
            <Text key={i} mih={16}>
              {x || " "}
            </Text>
          ))}

        {(modelProfile?.file_id || file_id) && (
          <MessageAttachments
            item={{ attachments: [attachedFile] }}
            isPreview
            isLinkedin
          />
        )}
      </Paper>
      <Text ta={"right"} size="sm" c={"dimmed"} w={"100%"} mb={"lg"}>
        {new Date().toLocaleString(lang.id === "FRA" ? "fr-FR" : "en-EN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hourCycle: "h23",
        })}
      </Text>
    </Stack>
  );
};

export default MessagePreviewer;
